<div class="max-w-4xl mx-auto p-2 lg:p-8 min-h-screen">
    <p-toast></p-toast>
    <p-confirmDialog></p-confirmDialog>
     <!-- Loading Overlay -->
     <div *ngIf="isFormLoading" class="fixed inset-0 bg-white bg-opacity-90 flex flex-col items-center justify-center z-50">
        <p-progressSpinner 
            [style]="{'width': '60px', 'height': '60px'}" 
            strokeWidth="4" 
            fill="transparent" 
            animationDuration="1s">
        </p-progressSpinner>
        <p class="text-gray-600 mt-4 text-lg" i18n>Loading form data...</p>
    </div>

    <div class="text-start mb-8">
        <h2 class="font-bold text-2xl mb-1" i18n>{{ isUpdateMode ? 'Update Contract' : 'Add New Contract' }}</h2>
        <p class="text-[#6B7280] text-sm m-0" i18n>{{ isUpdateMode ? 'Update Contract Details' : 'Create New Contract' }}</p>
    </div>

    <div class="space-y-6">
        <form [formGroup]="contractForm" class="space-y-8">
            <p-card styleClass="p-4">
                <p class="font-medium text-gray-800 mb-1" i18n>
                    Contract Information
                </p>
                
                <div class="space-y-4">
                    
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <!-- Customer Selection -->
                        <div class="flex flex-col gap-2">
                            <label for="customerId" class="font-medium text-[#6B7280] text-sm mt-1" i18n>
                                Customer *
                                <span *ngIf="isReservationLoaded" class="text-xs text-blue-600 ml-1">(Pre-selected)</span>
                            </label>
                            <p-select id="customerId" formControlName="customerId"
                                [options]="customerOptions"
                                optionLabel="label" optionValue="value"
                                placeholder="Select customer" i18n-placeholder
                                [disabled]="isReservationLoaded"
                                [class]="isReservationLoaded ? 'w-full rounded-lg border border-gray-300 text-base bg-gray-100 cursor-not-allowed' : 'w-full rounded-lg border border-gray-300 text-base transition-all duration-200 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 invalid:border-red-500 invalid:focus:ring-red-100'">
                            </p-select>
                            <button 
                                *ngIf="isReservationLoaded && contractForm.get('customerId')?.disabled"
                                type="button"
                                class="text-xs text-blue-600 hover:text-blue-800 mt-1"
                                (click)="contractForm.get('customerId')?.enable(); isReservationLoaded = false;"
                                i18n>
                                Change customer
                            </button>
                            
                        </div>
                        <!-- Empty div to maintain grid layout -->
                        <div></div>
                    </div>

                    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <!-- Product Selection -->
                        <div class="flex flex-col gap-2">
                            <label for="products" class="font-medium text-sm text-[#374151] mb-1" i18n>
                                Products
                                <span *ngIf="isReservationLoaded" class="text-xs text-blue-600 ml-1">(Pre-selected from reservation - can remove but not add)</span>
                            </label>
                            
                            <!-- Product Selection (only show if not from reservation) -->
                            <div *ngIf="!isReservationLoaded" class="card flex justify-center gap-3">
                                <p-select 
                                [ngModel]="selectedProduct"
                                [ngModelOptions]="{standalone: true}"
                                [options]="productsOptions"
                                optionLabel="label"
                                [filter]="true"
                                filterBy="label"
                                [showClear]="true"
                                i18n-placeholder
                                placeholder="Select a Product"
                                [disabled]="isEditMode"
                                (onChange)="onProductChange($event)"
                                class="w-full rounded-lg border border-gray-300 text-base transition-all duration-200 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100">
                                </p-select>
                                <p-button 
                                i18n-label
                                label="Add" 
                                severity="secondary" 
                                [disabled]="!selectedProduct || isEditMode"
                                (click)="addSelectedProduct()"
                               >
                                </p-button>
                            </div>
                            
                            <!-- Info message for reservation mode -->
                            <div *ngIf="isReservationLoaded" class="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                <p class="text-sm text-blue-700 mb-2" i18n>
                                    <i class="pi pi-info-circle mr-2"></i>
                                    Products are pre-selected from the reservation. You can remove products but cannot add new ones.
                                </p>
                                <div *ngIf="!hasSelectedProducts() && isReservationLoaded" class="mt-2">
                                    <p class="text-sm text-orange-600" i18n>
                                        <i class="pi pi-exclamation-triangle mr-2"></i>
                                        No products found in reservation.
                                    </p>
                                </div>
                            </div>
                            
                            <!-- Selected Products Display -->
                            <div class="flex flex-wrap gap-2 industry-region-tags">
                                <div *ngFor="let product of selectedProducts" class="tag">
                                    <span class="tag-text">{{ product.label }}</span>
                                    <button class="remove-button" 
                                            [disabled]="isEditMode" 
                                            (click)="removeProduct(product)"
                                            i18n-title
                                            aria-label="Remove product" i18n-aria-label
                                            [title]="isReservationLoaded ? 'Remove product' : 'Remove product'">×</button>
                                </div>
                            </div>

                            <!-- Product Values Input (only show when multiple products are selected) -->
                            <div *ngIf="hasMultipleProducts() && (!isEditMode || (isEditMode && isContractRejected() && isFSR()))" class="mt-4 space-y-3">
                                <p class="text-sm font-medium text-gray-700 mb-2" i18n>
                                    Enter value for each product:
                                </p>
                                <div *ngFor="let product of selectedProducts" class="flex flex-col gap-1">
                                    <label class="text-xs font-medium text-gray-600" i18n>
                                        {{ product.label }} Value (€)
                                    </label>
                                    <p-inputNumber 
                                        [(ngModel)]="productValues[product.value]"
                                        [ngModelOptions]="{standalone: true}"
                                        (ngModelChange)="onProductValueChange(product.value, $event)"
                                        placeholder="Enter value" i18n-placeholder
                                        [min]="0"
                                        [step]="0.01"
                                        [useGrouping]="false"
                                        mode="decimal"
                                        [minFractionDigits]="2"
                                        [maxFractionDigits]="2"
                                        styleClass="w-full"
                                        inputStyleClass="w-full rounded-lg border border-gray-300 text-base px-3 py-2 transition-all duration-200 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100">
                                    </p-inputNumber>
                                </div>
                            </div>
                        </div>
                        <!-- Contract Value Selection -->
                        <div class="flex flex-col gap-2">
                            <label for="contractValue" class="font-medium text-[#6B7280] text-sm mt-1" i18n>
                                Contract Value (€) <span *ngIf="!isEditMode || isContractRejected()" class="text-red-500">*</span>
                            </label>
                            <p-inputNumber 
                                id="contractValue"
                                formControlName="contractValue"
                                placeholder="Enter contract value" i18n-placeholder
                                [disabled]="hasMultipleProducts()"
                                [min]="0"
                                [step]="0.01"
                                [useGrouping]="false"
                                mode="decimal"
                                [minFractionDigits]="2"
                                [maxFractionDigits]="2"
                                [class.bg-gray-100]="hasMultipleProducts()"
                                styleClass="w-full"
                                inputStyleClass="w-full rounded-lg border border-gray-300 text-base transition-all duration-200 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 invalid:border-red-500 invalid:focus:ring-red-100">
                            </p-inputNumber>
                            <small *ngIf="hasMultipleProducts()" class="text-xs text-gray-500" i18n>
                                This value is automatically calculated from product values above.
                            </small>
                        </div>
                    </div>

                    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <!-- Duration Selection (Create Mode) -->
                        <div class="flex flex-col gap-2" *ngIf="!isEditMode">
                            <label for="duration" class="font-medium text-[#6B7280] text-sm mt-1" i18n>
                                Select Duration <span *ngIf="!isEditMode" class="text-red-500">*</span>
                            </label>
                            <p-select id="duration" formControlName="duration"
                                [options]="contractDurationsOptions"
                                optionLabel="label" optionValue="value"
                                placeholder="Select duration" i18n-placeholder
                                class="w-full rounded-lg border border-gray-300 text-base transition-all duration-200 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 invalid:border-red-500 invalid:focus:ring-red-100">
                            </p-select>
                        </div>
                         <!-- Start date Selection -->
                         <div class="flex flex-col gap-2">
                            <label for="startDate" class="font-medium text-[#6B7280] text-sm mt-1" i18n>
                                Start Date <span *ngIf="!isEditMode || isContractRejected()" class="text-red-500">*</span>
                            </label>
                            <input id="startDate" type="date" pInputText formControlName="startDate"
                                placeholder="Enter start date" i18n-placeholder
                                class="w-full rounded-lg border border-gray-300 text-base transition-all duration-200 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 invalid:border-red-500 invalid:focus:ring-red-100" />
                           
                        </div>
                        <!-- End Date Selection (Edit Mode) -->
                        <div class="flex flex-col gap-2" *ngIf="isEditMode">
                            <label for="endDate" class="font-medium text-[#6B7280] text-sm mt-1" i18n>
                                End Date <span *ngIf="isContractRejected()" class="text-red-500">*</span>
                            </label>
                            <input id="endDate" type="date" pInputText formControlName="endDate"
                                placeholder="Enter end date" i18n-placeholder
                                class="w-full rounded-lg border border-gray-300 text-base transition-all duration-200 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 invalid:border-red-500 invalid:focus:ring-red-100" />
                        </div>
                       
                    </div>

                    <div class="grid grid-cols-1 gap-6">
                        <!-- Contract Description -->
                        <div class="flex flex-col">
                            <label for="description" class="font-medium text-[#6B7280] text-sm mt-1 z-10" i18n>
                                Contract Description
                            </label>
                            <p-editor 
                                id="description"
                                formControlName="description"
                                [placeholder]="'Enter contract description and terms...'" i18n-placeholder
                                [style]="getEditorConfig().style"
                                [formats]="getEditorConfig().formats"
                                [modules]="getEditorConfig().modules"
                                class="-mt-3">
                                <ng-template #header>
                                    <span class="ql-formats">
                                        <button type="button" class="ql-bold" aria-label="Bold" i18n-aria-label></button>
                                        <button type="button" class="ql-italic" aria-label="Italic" i18n-aria-label></button>
                                        <button type="button" class="ql-underline" aria-label="Underline" i18n-aria-label></button>
                                    </span>
                                    <span class="ql-formats">
                                        <button type="button" class="ql-list" value="ordered" aria-label="Ordered List" i18n-aria-label></button>
                                    </span>
                                    <span class="ql-formats">
                                        <button type="button" class="ql-link" aria-label="Link" i18n-aria-label></button>
                                    </span>
                                </ng-template>
                            </p-editor>
                        </div>
                    </div>
                </div>
            </p-card>

            <p-card>

                <!-- Document Upload -->
                <div class="mt-2">
                    <div class="flex items-center gap-2 mb-3">
                        <svg width="18" height="19" viewBox="0 0 18 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M10.125 4.09268V12.6251C10.125 13.2474 9.62227 13.7501 9 13.7501C8.37773 13.7501 7.875 13.2474 7.875 12.6251V4.09268L5.29453 6.67314C4.85508 7.1126 4.14141 7.1126 3.70195 6.67314C3.2625 6.23369 3.2625 5.52002 3.70195 5.08057L8.20195 0.580566C8.64141 0.141113 9.35508 0.141113 9.79453 0.580566L14.2945 5.08057C14.734 5.52002 14.734 6.23369 14.2945 6.67314C13.8551 7.1126 13.1414 7.1126 12.702 6.67314L10.125 4.09268ZM2.25 12.6251H6.75C6.75 13.8661 7.75898 14.8751 9 14.8751C10.241 14.8751 11.25 13.8661 11.25 12.6251H15.75C16.991 12.6251 18 13.6341 18 14.8751V16.0001C18 17.2411 16.991 18.2501 15.75 18.2501H2.25C1.00898 18.2501 0 17.2411 0 16.0001V14.8751C0 13.6341 1.00898 12.6251 2.25 12.6251ZM15.1875 16.2813C15.4113 16.2813 15.6259 16.1925 15.7841 16.0342C15.9424 15.876 16.0312 15.6614 16.0312 15.4376C16.0312 15.2138 15.9424 14.9992 15.7841 14.841C15.6259 14.6827 15.4113 14.5938 15.1875 14.5938C14.9637 14.5938 14.7491 14.6827 14.5909 14.841C14.4326 14.9992 14.3438 15.2138 14.3438 15.4376C14.3438 15.6614 14.4326 15.876 14.5909 16.0342C14.7491 16.1925 14.9637 16.2813 15.1875 16.2813Z" fill="#6B7280"/>
                            </svg>
                            
                        <p class="font-medium text-[#111827] text-lg" i18n>Upload Documents</p>
                    </div>

                    <div class="grid grid-cols-1 gap-6">
                        <div class="flex flex-col gap-2">
                            <!-- File Upload Progress -->
                            <div *ngIf="isFileUploading" class="mb-4">
                                <p-progressBar mode="indeterminate" styleClass="h-2"></p-progressBar>
                                <small class="text-gray-500 text-sm mt-2 block" i18n>Uploading file...</small>
                            </div>

                            <!-- Uploaded Files Display -->
                            <div *ngIf="getAllDocumentsForDisplay().length > 0 && !isFileUploading" class="mb-4">
                                <div class="flex items-center justify-between mb-3">
                                    <h5 class="text-sm font-medium text-gray-700" i18n>
                                        Documents ({{ getAllDocumentsForDisplay().length }})
                                        <span *ngIf="isUpdateMode" class="text-xs text-gray-500 ml-2">- Existing documents cannot be removed</span>
                                    </h5>
                                    <p-button 
                                        *ngIf="!isUpdateMode || newDocuments.length > 0"
                                        type="button" 
                                        label="{{ isUpdateMode ? 'Remove New' : 'Remove All' }}" i18n-label
                                        icon="pi pi-trash" 
                                        severity="danger" 
                                        size="small"
                                        (onClick)="removeAllUploadedFiles()" 
                                        styleClass="p-button-text">
                                    </p-button>
                                </div>
                                
                                <div class="space-y-2">
                                    <div *ngFor="let doc of getAllDocumentsForDisplay(); let i = index" 
                                         [class]="doc.isExisting ? 'p-3 bg-blue-50 border border-blue-200 rounded-lg' : 'p-3 bg-green-50 border border-green-200 rounded-lg'">
                                        <div class="flex items-center justify-between">
                                            <div class="flex items-center gap-3">
                                                <i [class]="doc.isExisting ? 'pi pi-file text-blue-600' : 'pi pi-file text-green-600'"></i>
                                                <div>
                                                    <span [class]="doc.isExisting ? 'text-blue-800 font-medium text-sm' : 'text-green-800 font-medium text-sm'">
                                                        {{ doc.name }}
                                                        <span *ngIf="doc.isExisting" class="text-xs text-blue-600 ml-2" i18n>(Existing)</span>
                                                    </span>
                                                    <p class="text-xs text-gray-500" *ngIf="doc.file">
                                                        {{ (doc.file.size / 1024 / 1024).toFixed(2) }} MB
                                                    </p>
                                                </div>
                                            </div>
                                            <p-button 
                                                *ngIf="!doc.isExisting"
                                                type="button" 
                                                icon="pi pi-times" 
                                                severity="secondary" 
                                                size="small"
                                                (onClick)="removeUploadedFile(doc.index)" 
                                                styleClass="p-button-text">
                                            </p-button>
                                            <span *ngIf="doc.isExisting" class="text-xs text-blue-600 font-medium">
                                                <span i18n>Cannot Remove</span>
                                            </span>
                                        </div>

                                        <!-- Show thumbnail only for image files -->
                                        <div *ngIf="doc.file && isImageFile(doc.file)" class="mt-3">
                                            <h6 class="text-xs font-medium text-gray-600 mb-2" i18n>Preview</h6>
                                            <div class="flex justify-center">
                                                <img [src]="isUpdateMode ? newDocumentUrls[doc.index - existingDocuments.length] : uploadedFileUrls[doc.index]" 
                                                     alt="File preview" i18n-alt
                                                     class="max-w-full h-auto max-h-32 object-contain border rounded shadow-sm" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Hidden documents field -->
                            <input type="hidden" formControlName="documents" />

                            <!-- Main File Upload with Drag & Drop -->
                            <div class="mb-4 p-4 border-2 border-dashed rounded-lg text-center drag-drop-area"
                                [class]="dragOver ? 'border-blue-500 bg-blue-50 drag-over' : 'border-gray-300'"
                                [class.loading]="isFileUploading" (dragover)="onDragOver($event)" (dragleave)="onDragLeave($event)"
                                (drop)="onDrop($event)">

                                <input type="file" accept="application/pdf,image/png,image/jpeg,image/jpg"
                                    (change)="onFileUpload($event)" class="hidden" [disabled]="isFileUploading" 
                                    id="file-input-schedule" multiple />

                                <div class="flex flex-col items-center justify-center py-4">
                                    <svg class="mb-2" width="38" height="27" viewBox="0 0 38 27" fill="none"
                                        xmlns="http://www.w3.org/2000/svg">
                                        <path
                                            d="M8.6875 26.875C4.0293 26.875 0.25 23.0957 0.25 18.4375C0.25 14.7578 2.60547 11.6289 5.88672 10.4746C5.88086 10.3164 5.875 10.1582 5.875 10C5.875 4.82031 10.0703 0.625 15.25 0.625C18.7246 0.625 21.7539 2.51172 23.377 5.32422C24.2676 4.72656 25.3457 4.375 26.5 4.375C29.6055 4.375 32.125 6.89453 32.125 10C32.125 10.7148 31.9902 11.3945 31.75 12.0273C35.1719 12.7188 37.75 15.748 37.75 19.375C37.75 23.5176 34.3926 26.875 30.25 26.875H8.6875ZM13.3164 14.1602C12.7656 14.7109 12.7656 15.6016 13.3164 16.1465C13.8672 16.6914 14.7578 16.6973 15.3027 16.1465L17.5879 13.8613V21.7188C17.5879 22.498 18.2148 23.125 18.9941 23.125C19.7734 23.125 20.4004 22.498 20.4004 21.7188V13.8613L22.6855 16.1465C23.2363 16.6973 24.127 16.6973 24.6719 16.1465C25.2168 15.5957 25.2227 14.7051 24.6719 14.1602L19.9844 9.47266C19.4336 8.92188 18.543 8.92188 17.998 9.47266L13.3105 14.1602H13.3164Z"
                                            fill="#9CA3AF" />
                                    </svg>
                                    <label for="file-input-schedule" class="cursor-pointer text-blue-600 hover:text-blue-800 font-medium"
                                        [class.opacity-50]="isFileUploading" [class.cursor-not-allowed]="isFileUploading">
                                        <span *ngIf="isFileUploading" i18n>Uploading...</span>
                                        <span *ngIf="!isFileUploading" i18n>Click to upload files or drag and drop</span>
                                    </label>
                                    <p class="text-sm text-gray-500 mt-2" i18n>
                                        PDF, PNG, JPG (MAX. 10MB each) - Multiple files supported
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                
                
            </p-card>

            <!-- Rejection History and Timeline (Only show in update mode) -->
            <div *ngIf="isUpdateMode && currentContract">
              <!-- Rejection History -->
              <p-card *ngIf="currentContract.rejectionHistory && currentContract.rejectionHistory.length > 0" styleClass="p-4 mb-6">
                <h4 class="text-lg font-semibold text-gray-800 mb-4" i18n>Rejection History</h4>
                <div class="space-y-4">
                  <div *ngFor="let rejection of currentContract.rejectionHistory" class="p-4 bg-red-50 border-l-4 border-red-500 rounded">
                    <div class="flex items-start justify-between mb-2">
                      <div class="flex items-center gap-2">
                        <i class="pi pi-times-circle text-red-500"></i>
                        <span class="font-semibold text-red-900" i18n>Rejected</span>
                      </div>
                      <span class="text-sm text-gray-600">{{ formatDateTime(rejection.rejectedAt) }}</span>
                    </div>
                    <div class="mt-2">
                      <p class="text-sm font-medium text-gray-700 mb-1" i18n>Reason:</p>
                      <p class="text-sm text-gray-800">{{ rejection.reason }}</p>
                    </div>
                    <div *ngIf="rejection.rejectedByName" class="mt-2 text-xs text-gray-600">
                      <span i18n>Rejected by:</span> {{ rejection.rejectedByName }}
                    </div>
                  </div>
                </div>
              </p-card>

              <!-- Submission Timeline -->
              <p-card *ngIf="currentContract.submissionHistory && currentContract.submissionHistory.length > 0" styleClass="p-4 mb-6">
                <h4 class="text-lg font-semibold text-gray-800 mb-4" i18n>Submission Timeline</h4>
                <div class="space-y-3">
                  <div *ngFor="let submission of currentContract.submissionHistory" class="flex items-start gap-3 p-3 bg-blue-50 border-l-4 border-blue-500 rounded">
                    <i class="pi pi-send text-blue-500 mt-1"></i>
                    <div class="flex-1">
                      <div class="flex items-center justify-between">
                        <span class="font-semibold text-blue-900" i18n>Submitted</span>
                        <span class="text-sm text-gray-600">{{ formatDateTime(submission.submittedAt) }}</span>
                      </div>
                      <p class="text-xs text-gray-600 mt-1" i18n>Status: {{ submission.status }}</p>
                    </div>
                  </div>
                </div>
              </p-card>
            </div>

            <div class="flex justify-end gap-3">
                <!-- Resubmit button for FSR users with rejected contracts -->
                <p-button 
                  *ngIf="isUpdateMode && isFSR() && isContractRejected()"
                  type="button" 
                  i18n-label
                  label="Resubmit For Approval" 
                  [loading]="isSubmitting"
                  [disabled]="contractForm.invalid"
                  (onClick)="resubmitContract()"
                  icon="pi pi-refresh"
                  severity="success"
                >
                </p-button>
                
                <p-button 
                  type="button" 
                  i18n-label
                  [label]="isUpdateMode ? 'Update Contract' : 'Submit For Approval'" 
                  [loading]="isSubmitting"
                  [disabled]="contractForm.invalid"
                  (onClick)="onSubmit()"
                  icon="pi pi-save"
                >
                </p-button>
              </div>
        </form>
    </div>
</div>